import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { downloadCatalogue } from '@/lib/api';
import { trackEvent } from '@/hooks/useAnalytics';

const LOGO_URL = "/assets/images/logos/main-logo.webp";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'Industries', path: '/industries' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Clients', path: '/clients' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-4 left-4 right-4 z-50 rounded-full transition-all duration-300 ${
        isScrolled ? 'glass nav-shadow' : 'bg-white/90 backdrop-blur-sm'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Shubha Lakshmi Enterprises" 
              width="160"
              height="48"
              fetchPriority="high"
              decoding="async"
              className="h-12 w-auto"
            />
            <div className="hidden sm:block">
              <span className="font-bold text-primary text-lg tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Shubha Lakshmi
              </span>
              <span className="block text-xs text-muted-foreground -mt-1">
                Enterprises
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-primary' : 'text-slate-600 hover:text-primary'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href={downloadCatalogue()} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => trackEvent('catalogue_download', { path: '/catalogue/download' })}
            >
              <Button
                variant="outline"
                className="rounded-full border-primary text-primary hover:bg-primary/5"
                data-testid="download-catalogue-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Catalogue
              </Button>
            </a>
            <Link to="/contact">
              <Button
                className="rounded-full bg-primary hover:bg-primary/90 btn-primary"
                data-testid="get-quote-btn"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-primary/5"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-primary" />
            ) : (
              <Menu className="w-6 h-6 text-primary" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-border/50 pt-4 animate-fade-in-up">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-slate-600 hover:bg-primary/5'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border/50">
                <a 
                  href={downloadCatalogue()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('catalogue_download', { path: '/catalogue/download' })}
                >
                  <Button
                    variant="outline"
                    className="w-full rounded-full border-primary text-primary"
                    data-testid="mobile-download-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Catalogue
                  </Button>
                </a>
                <Link to="/contact">
                  <Button className="w-full rounded-full bg-primary" data-testid="mobile-quote-btn">
                    Get Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
